import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["onToggleModal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useContext, useState, useRef, useEffect, useMemo, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Icon, SelectWrapper, Chip, Input, Select, Slider, FieldGroup, Text, Tooltip, Button, createUseStyles, useTheme } from '@Sage/hoi-poi-ui';
import ScrollBarsCustom from "./ScrollBarsCustom";
import { getCrossID } from "../../utils/filters";
import { isEqual } from "../../utils/objects";
import { getRange } from "../../utils/time";
import { useWeb5Context } from "../../lib/Web5Context";
import { FiltersContext } from "../Filters/Context";
import { operators } from "../Filters/operators.js";
import { rangeFields, defaultPlaceholders, defaultValues, customFieldsComponent } from "../Filters/constants";
import useFilters from "../Filters/hooks/useFilters";
import useAdvancedFilters from "../Filters/hooks/useAdvancedFilters";
import useAddModal from "../Filters/useAddModal";
import ModalOptions from "../ModalOptions";
import styles from "./styles";
const {
  EMPTY,
  NOT_EMPTY,
  GREATER,
  GREATER_OR_EQUAL,
  LESS,
  LESS_OR_EQUAL,
  TODAY,
  YESTERDAY,
  TOMORROW,
  THIS_WEEK,
  LAST_WEEK,
  NEXT_WEEK,
  THIS_MONTH,
  LAST_MONTH,
  NEXT_MONTH,
  THIS_QUARTER,
  LAST_QUARTER,
  NEXT_QUARTER,
  THIS_YEAR,
  LAST_YEAR,
  NEXT_YEAR,
  IN,
  NOT_IN
} = operators;
const useStyles = createUseStyles(styles, 'QuickFilters');
const inputFields = ['text', 'integer', 'decimal', 'currency', 'percent'];
var _ref6 = /*#__PURE__*/React.createElement(Icon, {
  name: "refresh",
  size: "medium"
});
const QuickFilters = /*#__PURE__*/memo(_ref => {
  let {
    className,
    useQuickView,
    filters = [],
    schema,
    onChange,
    onClearAll,
    values,
    views,
    selectedView,
    customView,
    onChangeView,
    onChangeCustomView,
    hasFiltersActive,
    getCanClearAll,
    filterDragging,
    chipSize,
    isSubfilter,
    onChangeOperator,
    operators = {},
    license,
    crossOperators,
    crossLabels,
    entity,
    defaultFilters,
    extraSchema,
    extraMultiSchema,
    onConfirmAdd,
    onDeleteMultiple,
    onResetVisibles,
    visibleFilters,
    selectorsSchema,
    labels = {},
    showAdd,
    useAddIcon,
    getAllowedOperators = () => {},
    getFieldsWithoutAdvancedFilters = () => {},
    getHasAdvancedFilters = () => {},
    onToggle,
    toggled = {},
    forceSelectAllTypes,
    entityToForceLabel
  } = _ref;
  const classes = useStyles();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [innerValues, setInnerValues] = useState(values || {});
  const oldInnerValues = useRef(null);
  const selectRefs = useRef({});
  const inputRefs = useRef({});
  const theme = useTheme();
  const [wrapperOverlayInnerStyle, setWrapperOverlayInnerStyle] = useState({
    maxHeight: window.innerHeight - 50,
    overflowY: 'auto'
  });
  const {
    t,
    language
  } = useWeb5Context();
  const {
    withActiveAdvancedFilters,
    setClearFilters,
    setWithActiveAdvancedFilters
  } = useContext(FiltersContext);
  const {
    hasFiltersApplied,
    renderFilters,
    renderSubfilters
  } = useFilters({
    classes,
    crossOperators,
    entity,
    getAllowedOperators,
    getFieldsWithoutAdvancedFilters,
    getHasAdvancedFilters,
    onToggle,
    operators,
    toggled,
    values,
    fromQuickFilters: true
  });
  const {
    getFieldOperator,
    getOperatorValues,
    hasAdvancedFilters
  } = useAdvancedFilters({
    operators,
    crossOperators,
    getAllowedOperators,
    getFieldsWithoutAdvancedFilters,
    getHasAdvancedFilters
  });
  const _useAddModal = useAddModal({
      entity,
      defaultFilters,
      extraSchema,
      extraMultiSchema,
      onConfirmAdd,
      onDeleteMultiple,
      onResetVisibles,
      visibleFilters,
      selectorsSchema,
      labels,
      forceSelectAllTypes
    }),
    {
      onToggleModal
    } = _useAddModal,
    modalOptionsProps = _objectWithoutProperties(_useAddModal, _excluded);
  const classNames = classnames(classes.root, className);
  useEffect(() => {
    window.addEventListener('resize', () => {
      setWrapperOverlayInnerStyle({
        maxHeight: window.innerHeight - 50,
        overflowY: 'auto'
      });
    });
  }, []);
  useEffect(() => {
    if (!isEqual(oldInnerValues === null || oldInnerValues === void 0 ? void 0 : oldInnerValues.current, values)) {
      setInnerValues(values);
    }
  }, [values, setInnerValues]);
  const finalSchema = useMemo(() => {
    return schema.filter(current => {
      const ID = current.crossId || current.id;
      return filters.includes(ID);
    });
  }, [schema, filters]);
  const getIsOpen = useCallback(fieldId => {
    return isOpen => {
      setOpenDropdowns(_objectSpread(_objectSpread({}, openDropdowns), {}, {
        [fieldId]: isOpen
      }));
    };
  }, [openDropdowns, setOpenDropdowns]);
  const getIsOpenValueList = useCallback(fieldId => {
    return isOpen => {
      setOpenDropdowns(_objectSpread(_objectSpread({}, openDropdowns), {}, {
        [fieldId]: isOpen
      }));
      setTimeout(() => {
        var _selectRefs$current;
        if (isOpen && selectRefs !== null && selectRefs !== void 0 && (_selectRefs$current = selectRefs.current) !== null && _selectRefs$current !== void 0 && _selectRefs$current[fieldId]) {
          selectRefs.current[fieldId].focus();
        }
      }, 50);
    };
  }, [openDropdowns, setOpenDropdowns]);
  const getIsOpenInput = useCallback(fieldId => {
    return isOpen => {
      setTimeout(() => {
        var _inputRefs$current;
        if (isOpen && inputRefs !== null && inputRefs !== void 0 && (_inputRefs$current = inputRefs.current) !== null && _inputRefs$current !== void 0 && _inputRefs$current[fieldId]) {
          inputRefs.current[fieldId].focus();
        }
      }, 50);
    };
  }, []);
  const commonOnChange = useCallback(_ref2 => {
    let {
      field,
      value,
      info,
      isGroupChange,
      operatorValue,
      crossEntity
    } = _ref2;
    let newValue = value;
    //oldInnerValues to control wether we need to reset "innerValues" or not with "values" coming from props
    //we do it before any format process because we need those values to be equal to those that come from props

    const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    const crossId = getCrossID(field.id, finalEntity);
    const advancedFilters = getHasAdvancedFilters(finalEntity);
    if (advancedFilters && field.dataType === 'probability') {
      const currentOperators = !!finalEntity ? (crossOperators === null || crossOperators === void 0 ? void 0 : crossOperators[finalEntity]) || {} : operators;
      const currentOperatorValue = currentOperators[crossId] ? currentOperators[crossId].value : undefined;
      const newOperatorValue = operatorValue || currentOperatorValue;
      const oldValue = innerValues[crossId];
      let newMinValue = field.inputProps.min;
      let newMaxValue = field.inputProps.max;
      if ([LESS, LESS_OR_EQUAL].includes(newOperatorValue)) {
        newMinValue = Array.isArray(newValue) ? newValue[0] : newValue;
        newMaxValue = oldValue ? oldValue[1] : newMaxValue;
        newValue = [newMinValue, newMaxValue];
      }
      if ([GREATER, GREATER_OR_EQUAL].includes(newOperatorValue)) {
        newMinValue = oldValue ? oldValue[0] : newMinValue;
        newMaxValue = Array.isArray(newValue) ? newValue[1] : newValue;
        newValue = [newMinValue, newMaxValue];
      }
    }
    oldInnerValues.current = _objectSpread(_objectSpread({}, innerValues), {}, {
      [crossId]: newValue
    });

    // We work with "innerValues" over "values" always we can.
    // That way we prevent unnecessary rerenders.
    setInnerValues(_objectSpread(_objectSpread({}, innerValues), {}, {
      [crossId]: newValue
    }));
    if (isGroupChange) {
      if (value.filter(current => current).length > 0 && info !== null && info !== void 0 && info.action && (info === null || info === void 0 ? void 0 : info.action) === 'clear') {
        return;
      }
    }

    //Input fields only execute onChange when onBlur to make it more efficient
    if (inputFields.includes(field.dataType) && !!newValue && !(info !== null && info !== void 0 && info.action) && (info === null || info === void 0 ? void 0 : info.action) !== 'clear') {
      return;
    }
    onChange({
      filter: field,
      value: newValue,
      info,
      crossEntity: finalEntity
    });
  }, [getHasAdvancedFilters, innerValues, onChange, crossOperators, operators]);
  const handleOnChangeGroup = useCallback((field, crossEntity) => {
    const finalCrossEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    return (groupValue, value, index, event, info) => {
      commonOnChange({
        field,
        value: groupValue,
        info,
        isGroupChange: true,
        crossEntity: finalCrossEntity
      });
    };
  }, [commonOnChange]);
  const handleOnChange = useCallback(_ref3 => {
    let {
      field,
      operatorValue,
      crossEntity
    } = _ref3;
    const finalCrossEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    return (value, e, info) => {
      commonOnChange({
        field,
        value,
        info,
        operatorValue,
        crossEntity: finalCrossEntity
      });
    };
  }, [commonOnChange]);
  const valueListHandleOnChange = useCallback((field, isMulti, crossEntity) => {
    const crossId = getCrossID(field.id, crossEntity);
    return (value, info) => {
      var _selectRefs$current2;
      if (isMulti && !value && selectRefs !== null && selectRefs !== void 0 && (_selectRefs$current2 = selectRefs.current) !== null && _selectRefs$current2 !== void 0 && _selectRefs$current2[crossId]) {
        setTimeout(() => {
          selectRefs.current[crossId].focus();
        }, 50);
      }
      commonOnChange({
        field,
        value,
        info,
        crossEntity
      });
    };
  }, [commonOnChange]);
  const handleChangeOperator = useCallback(_ref4 => {
    let {
      field,
      defaultValue,
      crossEntity
    } = _ref4;
    return value => {
      const newValue = value || defaultValue;
      onChangeOperator && onChangeOperator({
        filter: field,
        value: newValue,
        crossEntity
      });
      if ([EMPTY, NOT_EMPTY].includes(newValue.value)) {
        handleOnChange({
          field,
          crossEntity
        })(defaultValues[field.dataType]);
      } else if (field.dataType === 'date') {
        handleOnChange({
          field,
          crossEntity
        })(defaultValues[field.dataType]);
      }
    };
  }, [handleOnChange, onChangeOperator]);
  const handleOnClearFilter = useCallback((field, operatorDefaultValue, crossEntity) => {
    return e => {
      e.stopPropagation();
      commonOnChange({
        field,
        value: defaultValues[field.dataType],
        crossEntity
      });
      operatorDefaultValue && handleChangeOperator({
        field,
        crossEntity
      })(operatorDefaultValue);
    };
  }, [commonOnChange, handleChangeOperator]);
  const handleOnBlur = useCallback((field, crossEntity) => {
    return value => {
      let operatorValue;
      const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
      const crossId = getCrossID(field.id, finalEntity);
      const advancedFilters = getHasAdvancedFilters(finalEntity);
      if (advancedFilters) {
        const currentOperators = !!finalEntity ? (crossOperators === null || crossOperators === void 0 ? void 0 : crossOperators[finalEntity]) || {} : operators;
        operatorValue = currentOperators[field.id] ? currentOperators[field.id].value : undefined;
      }
      if (rangeFields[field.dataType]) {
        var _innerValues$crossId;
        const innerValue = (_innerValues$crossId = innerValues[crossId]) === null || _innerValues$crossId === void 0 ? void 0 : _innerValues$crossId.filter(current => current).length;
        if (innerValue === 0 || innerValue === 2 || operatorValue && innerValue > 0 && [LESS, LESS_OR_EQUAL, GREATER, GREATER_OR_EQUAL].includes(operatorValue)) {
          return onChange({
            filter: field,
            value: innerValues[crossId] || null,
            crossEntity
          });
        }
        return;
      }
      return onChange({
        filter: field,
        value,
        crossEntity
      });
    };
  }, [getHasAdvancedFilters, onChange, crossOperators, operators, innerValues]);
  const renderChip = useCallback(_ref5 => {
    var _overrides$CloseIcon, _overrides$DividerIco, _field$operator3;
    let {
      isOpen,
      finalValue,
      actionValues,
      field,
      showFilterNumber = true,
      showSelectedValue,
      className,
      onRemove,
      overrides,
      isDate,
      entity
    } = _ref5;
    let numberFilters = '';
    let selectedValue = '';
    let isEmpty = false;
    let chipLabel = field.label;
    let crossLabelEntity = !!entity && crossLabels !== null && crossLabels !== void 0 && crossLabels[entity] ? `(${crossLabels[entity]})` : null;
    if (!crossLabelEntity && entityToForceLabel !== null && entityToForceLabel !== void 0 && entityToForceLabel.entity) {
      crossLabelEntity = `(${crossLabels[entityToForceLabel.entity]})`;
    }
    if (!!crossLabelEntity) chipLabel = `${chipLabel} ${crossLabelEntity}`;
    if (isDate) isEmpty = finalValue.every(el => el === null);
    if (finalValue && showFilterNumber) {
      if (Array.isArray(finalValue)) {
        numberFilters = finalValue.length;
      } else {
        numberFilters = 1;
      }
    }
    if (finalValue && showSelectedValue) {
      selectedValue = Array.isArray(finalValue) ? finalValue[0].label : finalValue.label;
    }
    if (numberFilters && showSelectedValue) {
      chipLabel = !!crossLabelEntity ? `${selectedValue} ${crossLabelEntity}` : selectedValue;
    } else if (numberFilters && numberFilters > 1 && !isDate) {
      chipLabel = !!crossLabelEntity ? `${field.label} ${crossLabelEntity} (${numberFilters})` : `${field.label} (${numberFilters})`;
    } else if (numberFilters && numberFilters === 1 && !isDate) {
      var _finalValue$;
      const chipLabelInner = ((_finalValue$ = finalValue[0]) === null || _finalValue$ === void 0 ? void 0 : _finalValue$.label) || (finalValue === null || finalValue === void 0 ? void 0 : finalValue.label);
      chipLabel = /*#__PURE__*/React.createElement("span", {
        className: classes.singleSelected
      }, !!crossLabelEntity ? `${chipLabelInner} ${crossLabelEntity}` : chipLabelInner);
    } else if (isDate && !isEmpty) {
      var _field$operator, _field$operator2;
      const chipLabelInner = (field === null || field === void 0 ? void 0 : (_field$operator = field.operator) === null || _field$operator === void 0 ? void 0 : _field$operator.label) || (field === null || field === void 0 ? void 0 : (_field$operator2 = field.operator) === null || _field$operator2 === void 0 ? void 0 : _field$operator2.value) || t('label_advanced_filter_option_inside_range');
      chipLabel = /*#__PURE__*/React.createElement("span", {
        className: classes.singleSelected
      }, !!crossLabelEntity ? `${chipLabelInner} ${crossLabelEntity}` : chipLabelInner);
    }
    if (overrides !== null && overrides !== void 0 && (_overrides$CloseIcon = overrides.CloseIcon) !== null && _overrides$CloseIcon !== void 0 && _overrides$CloseIcon.getClassName) {
      overrides.CloseIcon.className = overrides.CloseIcon.getClassName() || overrides.CloseIcon.className || '';
    }
    if (overrides !== null && overrides !== void 0 && (_overrides$DividerIco = overrides.DividerIcon) !== null && _overrides$DividerIco !== void 0 && _overrides$DividerIco.getClassName) {
      overrides.DividerIcon.className = overrides.DividerIcon.getClassName() || overrides.DividerIcon.className || '';
    }
    let isActive = isOpen || false;
    let isFilled = Array.isArray(finalValue) ? finalValue === null || finalValue === void 0 ? void 0 : finalValue.length : !!finalValue || false;
    if (rangeFields[field.dataType]) isFilled = finalValue === null || finalValue === void 0 ? void 0 : finalValue.some(Boolean);

    // Force filled state if dragging
    if (field.id && field.id === filterDragging) {
      isActive = true;
      isFilled = true;
    }
    const advancedFilters = getHasAdvancedFilters(entity);
    const hasOperator = advancedFilters && !!field.operator && ![IN, NOT_IN].includes(field === null || field === void 0 ? void 0 : (_field$operator3 = field.operator) === null || _field$operator3 === void 0 ? void 0 : _field$operator3.value);
    const isOutlinedColor = isSubfilter || false;
    const chipComp = /*#__PURE__*/React.createElement(Chip, {
      size: chipSize || 'large',
      isUnfolded: true,
      isActive: isActive,
      isFilled: !!isFilled || hasOperator || (actionValues === null || actionValues === void 0 ? void 0 : actionValues.length) > 0,
      isOutlinedColor: isOutlinedColor,
      className: className,
      onRemove: onRemove,
      overrides: overrides
    }, chipLabel);
    if (license && license.includes('free') && (field === null || field === void 0 ? void 0 : field.id) === 'view') {
      return /*#__PURE__*/React.createElement(Tooltip, {
        placement: "top",
        content: t('helptext_views_accounts')
      }, chipComp);
    } else if (numberFilters && numberFilters === 1 || isDate && !isEmpty) {
      let tooltipLabel = field.label;
      if (!!entity && crossLabels !== null && crossLabels !== void 0 && crossLabels[entity]) tooltipLabel = `${field.label} (${crossLabels[entity]})`;
      return /*#__PURE__*/React.createElement(Tooltip, {
        placement: "top",
        content: tooltipLabel
      }, chipComp);
    } else {
      return chipComp;
    }
  }, [crossLabels, filterDragging, getHasAdvancedFilters, isSubfilter, chipSize, license, classes.singleSelected, t, entityToForceLabel]);
  const selectOverrides = useMemo(() => ({
    styles: {
      menu: base => _objectSpread(_objectSpread({}, base), {}, {
        position: 'relative',
        boxShadow: 'none !important',
        margin: 0,
        paddingBottom: 0
      }),
      menuList: base => _objectSpread(_objectSpread({}, base), {}, {
        paddingBottom: 0
      }, theme.utils.scrollbar)
    },
    option: {
      style: {
        borderRadius: 4
      }
    }
  }), [theme]);
  const renderField = useCallback((field, crossEntity, subfilterSchema) => {
    var _commonProps$actions, _toggled$crossId, _commonProps$override2;
    if (!field.inputProps) field.inputProps = {};
    const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    const crossId = getCrossID(field.id, finalEntity);
    const advancedFilters = getHasAdvancedFilters(finalEntity);
    let commonProps = _objectSpread({
      key: crossId,
      labelMode: 'vertical'
    }, field.inputProps);
    let FilterField = null;
    let OperatorField = null;
    let operatorValue = undefined;
    let operatorDefaultValue = undefined;
    let defaultValue = defaultValues[field.dataType];
    if (rangeFields[field.dataType]) defaultValue = defaultValues['range'];

    // There are some actions that can activate filters and we need
    // to control the chip ui also for these filters
    const actionValues = (commonProps === null || commonProps === void 0 ? void 0 : (_commonProps$actions = commonProps.actions) === null || _commonProps$actions === void 0 ? void 0 : _commonProps$actions.length) && commonProps.actions.reduce((arr, current) => {
      if (innerValues[current.id]) {
        arr.push({
          id: current.id,
          value: innerValues[current.id]
        });
      }
      return arr;
    }, []) || null;
    let finalValue = (innerValues === null || innerValues === void 0 ? void 0 : innerValues[crossId]) || defaultValue;
    const isOpenDropDown = (openDropdowns === null || openDropdowns === void 0 ? void 0 : openDropdowns[crossId]) || false;
    if (hasAdvancedFilters(_objectSpread(_objectSpread({}, field), {}, {
      crossEntity: finalEntity
    }))) {
      operatorValue = getFieldOperator(field, finalEntity);
      const {
        options,
        value,
        defaultValue: defaultValueTemp
      } = getOperatorValues(field.dataType, operatorValue, finalEntity);
      operatorDefaultValue = defaultValueTemp;
      const isDefaultOperator = value.value === operatorDefaultValue.value;
      const overrides = isDefaultOperator ? {
        control: {
          style: {
            borderColor: `${theme.colors.grey[100]} !important`,
            backgroundColor: theme.colors.primary.white
          }
        }
      } : {
        control: {
          style: {
            borderColor: 'transparent !important',
            backgroundColor: theme.colors.semantic.infoCustom50,
            '&:hover': {
              backgroundColor: theme.colors.semantic.infoCustom100
            },
            cursor: 'pointer'
          }
        },
        controlFocused: {
          style: {
            borderColor: 'transparent !important',
            backgroundColor: theme.colors.semantic.infoCustom100
          }
        },
        indicatorsContainer: {
          style: {
            '& svg path': {
              fill: `${theme.colors.semantic.info500} !important`
            },
            '& .hoi-poi-select__indicator-separator': {
              backgroundColor: theme.colors.semantic.info500
            }
          }
        },
        valueContainer: {
          style: {
            '& .hoi-poi-select__single-value': {
              color: theme.colors.semantic.info500
            }
          }
        }
      };
      const operatorFieldProps = {
        key: `${crossId}__operator`,
        isRequired: isDefaultOperator,
        isSearchable: false,
        isFullWidth: true,
        className: classnames('fm-adv-filter-operator', classes.advancedFilter),
        clearIcon: !isDefaultOperator ? _ref6 : null,
        onChange: handleChangeOperator({
          field,
          defaultValue: operatorDefaultValue,
          crossEntity: finalEntity
        }),
        options,
        value,
        inputProps: {
          name: 'search'
        },
        overrides
      };
      OperatorField = /*#__PURE__*/React.createElement(Select, operatorFieldProps);
    }
    const isDisabled = advancedFilters && [EMPTY, NOT_EMPTY, LAST_WEEK, LAST_MONTH].includes(operatorValue);
    const subfiltersProps = {
      className: classes.subFilters,
      handleChangeOperator,
      handleOnBlur,
      handleOnChange,
      handleOnChangeGroup,
      innerValues,
      parentFilter: crossId,
      renderer: renderFilters,
      schema: subfilterSchema,
      toggled: (toggled === null || toggled === void 0 ? void 0 : (_toggled$crossId = toggled[crossId]) === null || _toggled$crossId === void 0 ? void 0 : _toggled$crossId.subfilters) || {}
    };
    if (field.overrideDefaultComponent) {
      var _commonProps$override;
      const FieldComponent = customFieldsComponent[field.overrideDefaultComponent];
      const fieldComponentProps = {
        crossEntity: finalEntity,
        field,
        values: innerValues,
        key: crossId,
        onChange: handleOnChange,
        onBlur: handleOnBlur,
        operator: operatorValue,
        isQuickfilter: true
      };
      const onRemove = Array.isArray(finalValue) && finalValue.length === 0 || !finalValue ? null : handleOnClearFilter(field, operatorDefaultValue, finalEntity);
      FilterField = /*#__PURE__*/React.createElement(SelectWrapper, {
        className: classes.selectWrapper,
        classes: {
          Popover: classes.popoverCustomWide
        },
        getIsOpen: getIsOpenValueList(crossId),
        overlayInnerStyle: wrapperOverlayInnerStyle,
        customOptions: /*#__PURE__*/React.createElement(Fragment, null, OperatorField, /*#__PURE__*/React.createElement(FieldComponent, fieldComponentProps))
      }, renderChip({
        isOpen: isOpenDropDown,
        finalValue,
        actionValues,
        field,
        onRemove,
        overrides: ((_commonProps$override = commonProps.overrides) === null || _commonProps$override === void 0 ? void 0 : _commonProps$override.chip) || {},
        entity: finalEntity
      }));
    } else {
      switch (field.dataType) {
        case 'bool':
          FilterField = /*#__PURE__*/React.createElement(SelectWrapper, {
            className: classes.selectWrapper,
            getIsOpen: getIsOpen(crossId),
            customOptions: /*#__PURE__*/React.createElement(Fragment, null, OperatorField, /*#__PURE__*/React.createElement(Select, _extends({}, commonProps, {
              value: finalValue,
              onChange: handleOnChange({
                field,
                crossEntity: finalEntity
              }),
              isFullWidth: true,
              labelMode: "horizontal",
              isReadOnly: isDisabled
            })), renderSubfilters(subfiltersProps))
          }, renderChip({
            isOpen: isOpenDropDown,
            finalValue,
            field,
            showFilterNumber: false,
            entity: finalEntity
          }));
          break;
        case 'probability':
          const isLessOperator = [LESS, LESS_OR_EQUAL].includes(operatorValue);
          const isGreaterOperator = [GREATER, GREATER_OR_EQUAL].includes(operatorValue);
          const isRange = !isLessOperator && !isGreaterOperator;
          if (isLessOperator) {
            finalValue = finalValue ? finalValue[0] : 0;
          }
          if (isGreaterOperator) {
            finalValue = finalValue ? finalValue[1] : 100;
          }
          FilterField = /*#__PURE__*/React.createElement(SelectWrapper, {
            className: classes.selectWrapper,
            classes: {
              Popover: classes.popoverCustomWide
            },
            getIsOpen: getIsOpen(crossId),
            customOptions: /*#__PURE__*/React.createElement(Fragment, null, OperatorField, /*#__PURE__*/React.createElement(Slider, _extends({
              isFullWidth: true,
              key: crossId,
              label: field.label,
              labelMode: "vertical",
              onChange: handleOnChange({
                field,
                operatorValue,
                crossEntity: finalEntity
              }),
              value: finalValue,
              min: 0,
              max: 100,
              step: 10,
              isRange: advancedFilters ? isRange : true,
              reverse: advancedFilters && isGreaterOperator,
              isReadOnly: isDisabled
            }, field.inputProps)), renderSubfilters(subfiltersProps))
          }, renderChip({
            isOpen: isOpenDropDown,
            finalValue,
            field,
            onRemove: finalValue ? handleOnClearFilter(field, operatorDefaultValue, finalEntity) : null,
            showFilterNumber: false,
            entity: finalEntity
          }));
          break;
        case 'singleValueList':
        case 'multipleValueList':
        case 'relatedValueList':
          if (!commonProps.placeholder) {
            commonProps.placeholder = defaultPlaceholders[field.dataType] || '';
          }
          let classNames = [classes.selectFuzzy];
          if (commonProps.className) classNames.push(commonProps.className);
          let finalOverrides = selectOverrides;
          if (commonProps.overrides) {
            finalOverrides = _objectSpread(_objectSpread({}, finalOverrides), commonProps.overrides);
          }
          if (isDisabled) commonProps.placeholder = '';
          if (!commonProps.highlightMatch && commonProps.isFuzzy) commonProps.highlightMatch = commonProps.isFuzzy;
          FilterField = /*#__PURE__*/React.createElement(SelectWrapper, {
            className: classes.selectWrapper,
            classes: {
              Popover: classes.popoverCustomWide
            },
            getIsOpen: getIsOpenValueList(crossId),
            overlayInnerStyle: wrapperOverlayInnerStyle,
            customOptions: /*#__PURE__*/React.createElement(Fragment, null, OperatorField, /*#__PURE__*/React.createElement(Select, _extends({}, commonProps, {
              className: classNames.join(' '),
              value: finalValue,
              onChange: valueListHandleOnChange(field, commonProps['isMulti'], finalEntity),
              isFullWidth: true,
              menuPosition: "absolute",
              useMenuPortal: false,
              getRef: ref => {
                selectRefs.current[crossId] = ref;
              },
              overrides: finalOverrides,
              isReadOnly: isDisabled,
              inputProps: {
                name: 'search'
              }
            })), renderSubfilters(subfiltersProps))
          }, renderChip({
            isOpen: isOpenDropDown,
            finalValue,
            actionValues,
            field,
            onRemove: finalValue ? handleOnClearFilter(field, operatorDefaultValue, finalEntity) : null,
            overrides: ((_commonProps$override2 = commonProps.overrides) === null || _commonProps$override2 === void 0 ? void 0 : _commonProps$override2.chip) || {},
            entity: finalEntity
          }));
          break;
        case 'text':
        default:
          commonProps.type = field.dataType;
          commonProps.onBlur = handleOnBlur(field, finalEntity);
          commonProps.isReadOnly = isDisabled;
          if (!commonProps.placeholder) {
            commonProps.placeholder = defaultPlaceholders[field.dataType] || '';
          }
          if (isDisabled) commonProps.placeholder = '';
          FilterField = /*#__PURE__*/React.createElement(SelectWrapper, {
            className: classes.selectWrapper,
            classes: {
              Popover: classes.popoverCustomWide
            },
            getIsOpen: getIsOpenInput(crossId),
            customOptions: /*#__PURE__*/React.createElement(Fragment, null, OperatorField, /*#__PURE__*/React.createElement(Input, _extends({}, commonProps, {
              value: finalValue,
              onChange: handleOnChange({
                field,
                crossEntity: finalEntity
              }),
              onEnter: handleOnBlur(field, finalEntity),
              isFullWidth: true,
              ref: ref => {
                inputRefs.current[field.id] = ref;
              }
            })), renderSubfilters(subfiltersProps))
          }, renderChip({
            isOpen: isOpenDropDown,
            finalValue,
            field,
            onRemove: finalValue ? handleOnClearFilter(field, operatorDefaultValue, finalEntity) : null,
            showFilterNumber: false,
            entity: finalEntity
          }));
          break;
      }
      if (rangeFields[field.dataType]) {
        var _field$locators, _field$locators2, _finalValue;
        const Field = rangeFields[field.dataType];
        if (field.dataType === 'date') {
          finalValue = [finalValue[0] || null, finalValue[1] || null];
        }
        const commonRange = {
          calendarButtonLabel: t('label_today'),
          onBlur: field.dataType !== 'date' ? handleOnBlur(field, finalEntity) : null,
          placeholder: commonProps.placeholder || defaultPlaceholders[field.dataType] || t('action_select'),
          lang: language
        };
        const inputOneIsReadOnly = advancedFilters && [EMPTY, NOT_EMPTY, TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR, GREATER, GREATER_OR_EQUAL].includes(operatorValue);
        const inputTwoIsReadOnly = advancedFilters && [EMPTY, NOT_EMPTY, TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR, LESS, LESS_OR_EQUAL].includes(operatorValue);
        const inputPropsOne = _objectSpread(_objectSpread(_objectSpread({}, commonProps), commonRange), {}, {
          key: `${crossId}From`,
          className: ((_field$locators = field.locators) === null || _field$locators === void 0 ? void 0 : _field$locators.first) || null,
          isReadOnly: inputOneIsReadOnly,
          overrides: {
            flatpickrOptions: {
              position: 'auto center'
            }
          }
        });
        const inputPropsTwo = _objectSpread(_objectSpread(_objectSpread({}, commonProps), commonRange), {}, {
          key: `${crossId}To`,
          className: ((_field$locators2 = field.locators) === null || _field$locators2 === void 0 ? void 0 : _field$locators2.last) || null,
          isReadOnly: inputTwoIsReadOnly,
          overrides: {
            flatpickrOptions: {
              position: 'auto center'
            }
          }
        });
        if (inputOneIsReadOnly) inputPropsOne.placeholder = '';
        if (inputTwoIsReadOnly) inputPropsTwo.placeholder = '';
        if (field.dataType === 'date') {
          var _inputPropsOne$maxDat, _inputPropsTwo$minDat;
          const minDate = finalValue[0] || null;
          const maxDate = finalValue[1] || null;
          inputPropsOne.maxDate = maxDate ? new Date(maxDate) : maxDate;
          inputPropsTwo.minDate = minDate ? new Date(minDate) : minDate;
          (_inputPropsOne$maxDat = inputPropsOne.maxDate) === null || _inputPropsOne$maxDat === void 0 ? void 0 : _inputPropsOne$maxDat.setHours(0, 0, 0, 0);
          (_inputPropsTwo$minDat = inputPropsTwo.minDate) === null || _inputPropsTwo$minDat === void 0 ? void 0 : _inputPropsTwo$minDat.setHours(0, 0, 0, 0);
          if (advancedFilters && [TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR].includes(operatorValue)) {
            finalValue = getRange(operatorValue);
            const currentValue = innerValues[crossId] || values[crossId];

            // Trigger onChange to set the predefined filter values
            // for the chosen operator if needed
            if (!currentValue || !!currentValue.length && currentValue[0].toString() !== finalValue[0].toString() || !!currentValue.length && currentValue[1].toString() !== finalValue[1].toString()) {
              handleOnChangeGroup(field, crossEntity)(finalValue);
            }
          }
        }
        return /*#__PURE__*/React.createElement("div", {
          key: crossId,
          className: classes.field
        }, /*#__PURE__*/React.createElement(SelectWrapper, {
          className: classes.selectWrapper,
          classes: {
            Popover: classes.popoverCustomWide
          },
          getIsOpen: getIsOpen(crossId),
          customOptions: /*#__PURE__*/React.createElement(Fragment, null, OperatorField, /*#__PURE__*/React.createElement(FieldGroup, {
            key: crossId,
            inputs: [Field, Field],
            isFullWidth: true,
            dividerText: "-",
            onChange: handleOnChangeGroup(field, finalEntity),
            value: finalValue,
            inputProps: [inputPropsOne, inputPropsTwo],
            classes: {
              divider: classes.fieldGroupDivider,
              rangeWrapper: classes.fieldGroupRangeWrapper
            }
          }), renderSubfilters(subfiltersProps))
        }, renderChip({
          isOpen: isOpenDropDown,
          finalValue,
          field,
          onRemove: (_finalValue = finalValue) !== null && _finalValue !== void 0 && _finalValue.some(Boolean) ? handleOnClearFilter(field, operatorDefaultValue, finalEntity) : null,
          showFilterNumber: true,
          isDate: true,
          entity: finalEntity
        })));
      }
    }
    return /*#__PURE__*/React.createElement("div", {
      key: crossId,
      className: classes.field
    }, FilterField);
  }, [getHasAdvancedFilters, innerValues, openDropdowns, hasAdvancedFilters, classes.subFilters, classes.field, classes.advancedFilter, classes.selectWrapper, classes.popoverCustomWide, classes.selectFuzzy, classes.fieldGroupDivider, classes.fieldGroupRangeWrapper, handleChangeOperator, handleOnBlur, handleOnChange, handleOnChangeGroup, renderFilters, toggled, getFieldOperator, getOperatorValues, theme, getIsOpen, renderSubfilters, renderChip, handleOnClearFilter, selectOverrides, getIsOpenValueList, wrapperOverlayInnerStyle, valueListHandleOnChange, getIsOpenInput, t, language, values]);
  const renderFields = useCallback((label, fields, index, crossEntity, subfilterSchema) => {
    return /*#__PURE__*/React.createElement(Fragment, {
      key: index
    }, (fields === null || fields === void 0 ? void 0 : fields.length) > 0 && fields.map(field => {
      if (!field.label) field.label = label;
      return renderField(field, crossEntity, subfilterSchema);
    }));
  }, [renderField]);
  const clearFiltersClassName = useMemo(() => {
    if (getCanClearAll) {
      if (!getCanClearAll()) return classes.actionsClearDisabled;
    } else if (!hasFiltersApplied && !hasFiltersActive && !withActiveAdvancedFilters) {
      return classes.actionsClearDisabled;
    }
    return classes.actionsClear;
  }, [getCanClearAll, hasFiltersApplied, hasFiltersActive, withActiveAdvancedFilters, classes.actionsClear, classes.actionsClearDisabled]);
  const handleClearAll = useCallback(() => {
    if (getCanClearAll) {
      if (getCanClearAll()) {
        onClearAll && onClearAll();
        setClearFilters(true);
        setWithActiveAdvancedFilters(false);
      }
    } else if (hasFiltersApplied || selectedView || hasFiltersActive || withActiveAdvancedFilters) {
      onClearAll && onClearAll();
      setClearFilters(true);
      setWithActiveAdvancedFilters(false);
    }
  }, [hasFiltersApplied, selectedView, hasFiltersActive, onClearAll, setClearFilters, getCanClearAll, withActiveAdvancedFilters, setWithActiveAdvancedFilters]);
  const handleClearView = useCallback(e => {
    e.stopPropagation();
    if (customView) {
      onChangeCustomView(null);
    } else {
      onChangeView(null);
    }
    setOpenDropdowns(_objectSpread(_objectSpread({}, openDropdowns), {}, {
      idView: false
    }));
  }, [customView, onChangeCustomView, onChangeView, openDropdowns, setOpenDropdowns]);
  let viewsClasses = [classes.views];
  if (!filters || (filters === null || filters === void 0 ? void 0 : filters.length) === 0) viewsClasses.push(classes.viewsAlone);
  const viewsChipClasses = useMemo(() => classnames(classes.viewsChip, {
    [classes.viewsChipFilled]: customView || selectedView
  }), [classes.viewsChip, classes.viewsChipFilled, customView, selectedView]);
  const hasActiveFilters = useMemo(() => {
    if (values && Object.keys(values).length > 0 || operators && Object.keys(operators).length) return true;
    const activeCrossOperators = crossOperators && Object.entries(crossOperators).reduce((num, _ref7) => {
      let [entity, operators] = _ref7;
      if (Object.keys(operators).length > 0) num++;
      return num;
    }, 0) || 0;
    if (activeCrossOperators > 0) return true;
    return false;
  }, [values, operators, crossOperators]);
  const addFiltersIconTooltip = useMemo(() => {
    let text = labels.addFields;
    if (labels !== null && labels !== void 0 && labels.addFields) return text;
    if (!(visibleFilters !== null && visibleFilters !== void 0 && visibleFilters.length)) text = t('label_add_filters');else text = t('label_edit_filters');
    return text;
  }, [visibleFilters, labels, t]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.left
  }, /*#__PURE__*/React.createElement(ScrollBarsCustom, null, useQuickView && /*#__PURE__*/React.createElement("div", {
    className: viewsClasses.join(' ')
  }, /*#__PURE__*/React.createElement(SelectWrapper, {
    className: classes.selectWrapper,
    classes: {
      Popover: classes.popoverCustomWide
    },
    options: views,
    getIsOpen: getIsOpen('idView'),
    value: selectedView,
    onChange: customView ? onChangeCustomView : onChangeView,
    truncateOptions: false
  }, renderChip({
    isOpen: (openDropdowns === null || openDropdowns === void 0 ? void 0 : openDropdowns['idView']) || false,
    finalValue: customView || selectedView || null,
    field: {
      id: 'view',
      label: t('label_view')
    },
    showSelectedValue: true,
    className: viewsChipClasses,
    isFilled: customView || selectedView || null,
    isFolded: true,
    onRemove: customView || selectedView ? handleClearView : null
  }))), useQuickView && (filters === null || filters === void 0 ? void 0 : filters.length) > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.divider
  }), finalSchema.length > 0 && finalSchema.map((current, index) => {
    return renderFields(current.label, current.fields, index, current.entity, current.subfilterSchema);
  })), onClearAll && (finalSchema === null || finalSchema === void 0 ? void 0 : finalSchema.length) !== 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.clearContainer
  }, /*#__PURE__*/React.createElement(Tooltip, {
    content: t('action_clear_filters')
  }, /*#__PURE__*/React.createElement(Text, {
    className: clearFiltersClassName,
    type: "body",
    onClick: handleClearAll
  }, t('action_clear'))))), showAdd && !useAddIcon && /*#__PURE__*/React.createElement("div", {
    className: classes.right
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.addButton
  }, /*#__PURE__*/React.createElement(Button, {
    isFullWidth: true,
    icon: "plus",
    type: "secondary",
    onClick: onToggleModal
  }, (labels === null || labels === void 0 ? void 0 : labels.addFields) || t('label_add_filters')))), showAdd && useAddIcon && /*#__PURE__*/React.createElement("div", {
    className: classes.right
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.addButtonIcon
  }, /*#__PURE__*/React.createElement(Icon, {
    name: hasActiveFilters ? 'filterListActive' : 'filterList',
    size: "large",
    onClick: onToggleModal,
    tooltip: addFiltersIconTooltip
  }))), /*#__PURE__*/React.createElement(ModalOptions, _extends({}, modalOptionsProps, {
    onToggleModal: onToggleModal
  })));
});
process.env.NODE_ENV !== "production" ? QuickFilters.propTypes = {
  className: PropTypes.string,
  useQuickView: PropTypes.bool,
  filters: PropTypes.array,
  schema: PropTypes.array,
  onChange: PropTypes.func,
  onClearAll: PropTypes.func,
  values: PropTypes.object,
  views: PropTypes.array,
  selectedView: PropTypes.object,
  customView: PropTypes.object,
  onChangeView: PropTypes.func,
  onChangeCustomView: PropTypes.func,
  hasFiltersActive: PropTypes.bool
} : void 0;
export default QuickFilters;